import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import { WorkflowAction } from 'models/WorkflowAction';
import { getBenefitAttachmentUrl } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';

import BenefitFileRow from './BenefitFileRow';
import { ManualBenefitsAttachment, ManualBenefitsRecord } from './eligibilityData';
import { useOpenSignedUrl } from './useOpenSignedUrl';

interface Props {
  workflowId: string;
  record: ManualBenefitsRecord;
  benefits: WorkflowAction;
  // Absent for a reader who may not change the record.
  onEdit?: () => void;
}

export default function ManualBenefitsView({ workflowId, record, benefits, onEdit }: Props) {
  const openSignedUrl = useOpenSignedUrl();

  const open = (file: ManualBenefitsAttachment) =>
    openSignedUrl(() => getBenefitAttachmentUrl(workflowId, file.id), file.contentType);

  return (
    <Container data-testid='manual-benefits-view'>
      {benefits.completedAt && (
        <Attribution data-testid='manual-benefits-attribution'>
          Benefits added manually{benefits.completedByName ? ` by ${benefits.completedByName}` : ''} on{' '}
          {simpleDateAtTime(benefits.completedAt)}
        </Attribution>
      )}
      <Card>
        {onEdit && (
          <EditLink type='button' onClick={onEdit} data-testid='manual-benefits-edit'>
            <PencilIcon color={colors.blue100} width={12} height={12} />
            Edit
          </EditLink>
        )}
        <Detail label='Payer'>{record.payer.name}</Detail>
        <Detail label='Member ID'>{record.memberId || '—'}</Detail>
        <Detail label='Files'>
          {record.attachments.length === 0 ? (
            '—'
          ) : (
            <Files>
              {record.attachments.map((file) => (
                <BenefitFileRow key={file.id} filename={file.filename} onOpen={() => open(file)} />
              ))}
            </Files>
          )}
        </Detail>
        {record.note && <Detail label='Notes'>{record.note}</Detail>}
      </Card>
    </Container>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <DetailLabel>{label}</DetailLabel>
      <DetailValue as='div'>{children}</DetailValue>
    </div>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Attribution = styled(BodySmall)`
  color: ${colors.black50};
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
`;

const DetailLabel = styled(BodySmall)`
  margin-bottom: 4px;
  color: ${colors.black50};
`;

const DetailValue = styled(BodySmall)`
  color: ${colors.black100};
  font-size: 14px;
  white-space: pre-wrap;
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 500px;
`;

const EditLink = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;
